import React, { Fragment, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Form from './Form';
import FrontCanvas from './FrontCanvas';
import './NrcEditor.scss';
import { Helmet } from 'react-helmet';
import Layout from './../Layout';
import * as selectors from './NrcEditor.selectors';
import * as actions from './NrcEditor.actions';
import { stringifyEqualityCheck } from './../../utils';
import * as loginSelectors from './../Login/Login.selectors';
import { Link } from 'react-router-dom';

const NrcEditor = (props) => {
    /* State to props */
    const templateCode = useSelector(selectors.getTemplateCode);
    const quantity = useSelector(selectors.getQuantity);
    const item = useSelector(selectors.getItem, stringifyEqualityCheck);
    const fetching = useSelector(selectors.getFetching);
    const addingToCart = useSelector(selectors.getAddingToCart);
    const cxmlData = useSelector(loginSelectors.getCxmlData, stringifyEqualityCheck);
    const notes = useSelector(selectors.getNotes);
    const image = useSelector(selectors.getImage);
    const product = useSelector(selectors.getProduct);
    const isFavorite = useSelector(selectors.getIsFavorite);
    const checkingFavorite = useSelector(selectors.getCheckingFavorite);
    const savingWork = useSelector(selectors.getSavingWork);
    const action = useSelector(selectors.getAction);
    const platformType = useSelector(loginSelectors.getPlatformType, stringifyEqualityCheck);

    /* Dispatch to props */
    const dispatch = useDispatch();
    const fetchItem = useCallback((id) => dispatch(actions.fetchItem(id)), [dispatch]);
    const setTemplateCode = useCallback((value) => dispatch(actions.setTemplateCode(value)), [dispatch]);
    const setQuantity = useCallback((value) => dispatch(actions.setQuantity(value)), [dispatch]);
    const resetState = useCallback(() => dispatch(actions.resetState()), [dispatch]);
    const addToCart = useCallback((orderType, history, type) => dispatch(actions.addToCart(orderType, history, type)), [dispatch]);
    const setNotes = useCallback((value) => dispatch(actions.setNotes(value)), [dispatch]);
    const setImage = useCallback((value) => dispatch(actions.setImage(value)), [dispatch]);
    const setProduct = useCallback((value) => dispatch(actions.setProduct(value)), [dispatch]);
    const addFavorite = useCallback((history) => dispatch(actions.addFavorite(history)), [dispatch]);
    const checkFavorite = useCallback((templateCode) => dispatch(actions.checkFavorite(templateCode)), [dispatch]);
    const setAction = useCallback((value) => dispatch(actions.setAction(value)), [dispatch]);
    const loadVariables = useCallback(() => dispatch(actions.loadVariables()), [dispatch]);

    /* componentDidMount */
    useEffect(() => {
        resetState();
        setTemplateCode(props.match.params.id);
        checkFavorite(props.match.params.id);
        setProduct(props.match.params.id);
        fetchItem(props.match.params.id);

        if (props.match.params.action === 'edit') {
            loadVariables();
            setAction('edit');
        } else if (props.match.params.action === 'reorder') {
            loadVariables();
            setAction('reorder');
        }

        return () => {
            resetState();
        };
    }, [setTemplateCode, resetState, fetchItem, setProduct, checkFavorite, loadVariables, setAction, 
        props.match.params.id, props.match.params.action]);

    let content = <div className="text-center"><i className="fas fa-circle-notch fa-spin" /> Loading data...</div>;
    if (!fetching) {
        content = (
            <div className="steps-content">
                <div className="left-side-container">
                    <Form
                        quantity={quantity}
                        cxmlData={cxmlData}
                        templateCode={templateCode}
                        item={item}
                        fetching={fetching}
                        addingToCart={addingToCart}
                        notes={notes}
                        image={image}
                        product={product}
                        history={props.history}
                        type={props.match.params.type}
                        setQuantity={setQuantity}
                        addToCart={addToCart}
                        setNotes={setNotes}
                        setImage={setImage}
                        isFavorite={isFavorite}
                        addFavorite={addFavorite}
                        checkingFavorite={checkingFavorite}
                        savingWork={savingWork}
                        action={action}
                    />
                </div>
                <div className="right-side-container">
                    <FrontCanvas
                        templateCode={templateCode}
                        image={image}
                        quantity={quantity}
                        templateId={props.match.params.id}
                        history={props.history}
                        item={item}
                        fetching={fetching}
                        addingToCart={addingToCart}
                        notes={notes}
                        product={product}
                        cxmlData={cxmlData}
                        addToCart={addToCart}
                        action={action}
                    />
                </div>
            </div>
        );
    }

    let breadcrumbsContainer = null;
    if (item != null) {
        const breadcrumbs = [<Link to={'/' + platformType}>Main Categories</Link>, ' / NRC'];
        breadcrumbsContainer = (
            <div className="breadcrumbs">/ {breadcrumbs}</div>
        );
    }

    return (
        <Layout
            title={<Fragment><i className="fas fa-paint-brush"></i> Editor</Fragment>}
            step={3}
            history={props.history}
        >
            <Helmet><title>Editor - UT Physicians</title></Helmet>
            <div className="nrc-editor-container">
                {breadcrumbsContainer}
                {content}
            </div>
        </Layout>
    );
};

export default NrcEditor;
